import React, { useRef } from 'react';
import { parseLRC } from '../utils/parser';

export default function MediaBin({ setAudioUrl, setBgMedia, setLyrics, bgMedia }) {
  const audioInputRef = useRef();
  const bgInputRef = useRef();
  const lrcInputRef = useRef();

  const handleAudioImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setAudioUrl(url);
    }
  };

  const handleBgImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      const isVideo = file.type.startsWith('video/');
      const el = document.createElement(isVideo ? 'video' : 'img');
      el.src = url;
      if (isVideo) {
        el.muted = true;
        el.loop = true;
      }
      setBgMedia(prev => ({ ...prev, type: isVideo ? 'video' : 'image', url, element: el }));
    }
  };

  const handleLrcImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const text = evt.target.result;
        const parsed = parseLRC(text);
        setLyrics(parsed);
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="p-5 flex flex-col gap-6">
      <div>
        <h2 className="text-lg font-bold mb-4">Media Bin</h2>
        
        <div className="space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded p-4 flex flex-col items-center justify-center text-center gap-2">
            <span className="text-sm font-medium">Master Audio</span>
            <button 
              onClick={() => audioInputRef.current?.click()}
              className="text-xs bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded w-full"
            >
              Select Audio File
            </button>
            <input type="file" accept="audio/*" ref={audioInputRef} className="hidden" onChange={handleAudioImport} />
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded p-4 flex flex-col items-center justify-center text-center gap-2">
            <span className="text-sm font-medium">Background Media</span>
            <div className="w-full h-24 bg-black rounded overflow-hidden mb-2 relative flex items-center justify-center text-xs text-gray-500">
              {bgMedia.type === 'color' && <div className="absolute inset-0" style={{backgroundColor: bgMedia.color}}></div>}
              {bgMedia.type === 'image' && <img src={bgMedia.url} className="absolute inset-0 w-full h-full object-cover" alt="bg"/>}
              {bgMedia.type === 'video' && <video src={bgMedia.url} className="absolute inset-0 w-full h-full object-cover"/>}
              {!bgMedia.url && "Color Fill"}
            </div>
            
            {bgMedia.type === 'color' && (
              <input 
                type="color" 
                value={bgMedia.color}
                onChange={e => setBgMedia(prev => ({...prev, color: e.target.value}))}
                className="w-full h-8 bg-transparent cursor-pointer mb-2"
              />
            )}
            
            <button 
              onClick={() => bgInputRef.current?.click()}
              className="text-xs bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded w-full"
            >
              Select Image/Video
            </button>
            <input type="file" accept="image/*,video/*" ref={bgInputRef} className="hidden" onChange={handleBgImport} />
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded p-4 flex flex-col gap-2">
            <span className="text-sm font-medium text-center">Overlay Filter</span>
            <select 
              value={bgMedia.filter || 'none'}
              onChange={e => setBgMedia(prev => ({...prev, filter: e.target.value}))}
              className="w-full bg-gray-800 border border-gray-700 rounded p-2 text-sm text-gray-200 focus:outline-none focus:border-emerald-500"
            >
              <option value="none">None</option>
              <option value="vignette">Vignette</option>
              <option value="film-grain">Film Grain</option>
              <option value="warm-fade">Warm Fade</option>
            </select>
          </div>
        </div>
      </div>

      <div className="h-px bg-gray-800 w-full"></div>

      <div>
        <h2 className="text-lg font-bold mb-4">Lyrics</h2>
        <div className="bg-gray-900 border border-gray-800 rounded p-4 flex flex-col text-center gap-2">
          <span className="text-sm text-gray-400">Import standard .lrc file to populate timeline with timestamps</span>
          <button 
            onClick={() => lrcInputRef.current?.click()}
            className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white font-medium px-3 py-1.5 rounded w-full mt-2"
          >
            Import .lrc File
          </button>
          <input type="file" accept=".lrc,text/plain" ref={lrcInputRef} className="hidden" onChange={handleLrcImport} />
        </div>
      </div>
    </div>
  );
}